import { useState, useEffect } from 'react'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import validator from 'validator'
import { SMARTHIRE_API } from './SmartHireAuth'
import 'react-phone-number-input/style.css'
import './SmartHireSignInModal.css'

const MODAL_TITLES: Record<string, { title: string; subtitle: string; icon: string }> = {
  'Book a Demo': { title: 'Book a Demo', subtitle: 'See PossoBuild SmartHire in action', icon: '📅' },
  'Book an Enterprise Demo': { title: 'Book an Enterprise Demo', subtitle: 'Tailored walkthrough for your organization', icon: '🏢' },
  'Start Your Pilot': { title: 'Start Your Pilot', subtitle: 'Test with your highest-volume roles', icon: '🚀' },
  'Start Free Trial': { title: 'Start Free Trial', subtitle: 'Get started with PossoBuild SmartHire', icon: '✨' },
  'Request a Demo': { title: 'Request a Demo', subtitle: 'Our team will reach out to schedule', icon: '📅' },
}
const DEFAULT_MODAL = { title: 'Book a Demo', subtitle: 'See PossoBuild SmartHire in action', icon: '📅' }

export default function BookDemoModal({ source, onClose }: { source: string; onClose: () => void }) {
  const modal = MODAL_TITLES[source] || DEFAULT_MODAL
  const [form, setForm] = useState<Record<string, string>>({ source: source || 'marketing-site' })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.name?.trim()) { setError('Please enter your name.'); return }
    if (!form.company?.trim()) { setError('Please enter your company name.'); return }
    if (!form.email || !validator.isEmail(form.email)) { setError('Please enter a valid email.'); return }
    if (!form.phone || !isValidPhoneNumber(form.phone)) { setError('Please enter a valid phone number.'); return }

    setSubmitting(true)
    try {
      const res = await fetch(`${SMARTHIRE_API}/contact_us_form`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      await res.json()
      setSuccess('Thank you for your interest in PossoBuild SmartHire! Our team will reach out soon to schedule a demo for you.')
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="sh-modal-backdrop" onClick={handleBackdropClick}>
      <div className="sh-modal">
        {/* Header */}
        <div className="sh-modal__header">
          <div className="sh-modal__title-wrap">
            <div className="sh-modal__icon-wrap sh-modal__icon-wrap--org">{modal.icon}</div>
            <div>
              <h2 className="sh-modal__title">{modal.title}</h2>
              <p className="sh-modal__subtitle">{modal.subtitle}</p>
            </div>
          </div>
          <button className="sh-modal__close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="sh-modal__body">
          {success ? (
            <div className="sh-modal__info">{success}</div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <div className="sh-modal__error">{error}</div>}

              <input
                className="sh-modal__input"
                type="text"
                placeholder="Name"
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />

              <input
                className="sh-modal__input"
                type="text"
                placeholder="Company"
                value={form.company || ''}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
              />

              <input
                className="sh-modal__input"
                type="email"
                placeholder="Work email"
                value={form.email || ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />

              <div className="sh-modal__phone-wrap">
                <PhoneInput
                  international
                  defaultCountry="US"
                  placeholder="Phone number"
                  value={form.phone || ''}
                  onChange={(val) => setForm({ ...form, phone: val || '' })}
                />
              </div>

              <button className="sh-modal__btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : `${modal.title} →`}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
